<template>
  <section
    ref="section"
    class="toggle-module-component"
    :style="{
      '--color-accent': `${data.theme.accent}`,
      '--color-line-t': `${data.theme.line_top}`,
      '--color-line-b': `${data.theme.line_bottom}`,
    }">
    <div
      class="content inner-container"
      :style="{
        background: `url(${data.bg.url})`,
        backgroundSize: 'cover',
        backgroundRepeat: 'none',
      }">
      <div class="left">
        <div
          class="left-content"
          :class="{ active: isOpen }">
          <h2
            class="left-content-title"
            v-html="data.left.title" />
          <div
            class="left-content-text"
            v-html="data.left.content" />
        </div>
        <div
          role="button"
          class="toggle-container"
          @click="toggle">
          <div class="button b2">
            <input
              type="checkbox"
              class="checkbox" />
            <div class="knobs">
              <span></span>
            </div>
            <div class="layer"></div>
          </div>
        </div>
      </div>
      <div
        class="right"
        :class="{ active: isOpen }">
        <div
          class="right-content"
          v-html="data.right" />
      </div>
    </div>
    <div class="vfx-container">
      <div class="vfx inner-container">
        <div class="line-w-circle">
          <div
            ref="line"
            class="line"
            :class="`line-l line-gradient`"></div>
          <div
            class="sphere-rails"
            :class="`line-l`">
            <div
              ref="sphere"
              class="sphere" />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
/* #META
 {
  "NAME": "ToggleComponent",
  "CLASSNAME": "toggle-module-component",
  "URL": "http://lifemine.local/templates/"
 }
#ENDMETA */
import { ref, onMounted, onBeforeUnmount } from 'vue'
import useUtils from '@/composables/useUtils'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
gsap.registerPlugin(ScrollTrigger)

const { genUUID } = useUtils()
const animID = ref(genUUID())
const isOpen = ref(false)
const sphere = ref()
const line = ref()
const section = ref()

const toggle = () => {
  isOpen.value = !isOpen.value
}

const sphereAnimIn = () => {
  gsap.fromTo(
    sphere.value,
    {
      y: -250,
    },
    {
      y: -100,
      ease: 'power1.out',
      scrollTrigger: {
        trigger: section.value,
        scrub: true,
        start: 'top center+=20%',
        end: 'top+=40% center',
        // markers: true,
        id: animID.value,
      },
    }
  )
}
const lineAnim = () => {
  gsap.fromTo(
    line.value,
    {
      height: '0%',
    },
    {
      height: '100%',
      scrollTrigger: {
        trigger: section.value,
        scrub: true,

        start: 'top+=100px center+=20%',
        end: 'bottom+=100px center+=20%',
        // markers: true,
        id: animID.value,
      },
    }
  )
}

onMounted(() => {
  lineAnim()
  sphereAnimIn()
})

onBeforeUnmount(() => {
  const anim = ScrollTrigger.getById(animID.value)
  if (anim) anim.kill()
})

defineProps(['data'])
</script>

<style lang="scss">
.toggle-module-component {
  @apply relative py-[25px] lg:py-[30px];

  .vfx-container {
    @apply absolute top-0 w-full h-full pointer-events-none;

    .vfx {
      @apply h-full w-full;

      .line-w-circle {
        @apply relative h-full w-full;

        .line,
        .sphere-rails {
          @apply absolute top-0 w-[1px] h-full flex items-center justify-center overflow-visible;

          &-l {
            @apply left-0;
          }

          &-gradient {
            background: linear-gradient(
              171deg,
              var(--color-line-t) 0%,
              var(--color-line-b) 100%
            );
          }

          .sphere {
            @apply min-w-[30px] min-h-[30px] rounded-full bg-[var(--color-accent)] blur-[2px];
            box-shadow: 0 0 20px var(--color-accent);
          }
        }
      }
    }
  }

  .content {
    @apply relative px-0 max-w-[1324px] overflow-hidden;

    .left {
      @apply h-[520px] pl-[53px] pr-[43px] pt-[70px] pb-[60px] flex flex-col items-start justify-between lg:h-[600px] lg:pl-[86px] lg:pt-[156px] lg:pb-[95px] xl:h-[700px];

      &-content {
        @apply lg:w-[58%] flex flex-col gap-[30px] transition-all duration-500;

        &-title {
          p {
            @apply text-[34px] leading-[38px] tracking-[0.34px] hyphens-auto lg:hyphens-none lg:heading-3 lg:font-telegraf xl:heading-2;
          }

          strong {
            @apply font-[400] text-[var(--color-accent)];
          }
        }

        &-text {
          p {
            @apply large xl:heading-4;
          }
        }

        &.active {
          @apply lg:w-[44%] xl:hyphens-none;

          .left-content-title {
            p {
              @apply lg:hyphens-auto xl:hyphens-none;
            }
          }
        }
      }

      .toggle-container {
        .button {
          position: relative;
          top: 50%;
          width: 126px;
          height: 46px;
          margin: -20px auto 0 auto;
          overflow: hidden;
          background: var(--color-accent);
          border-radius: 50px !important;

          &.r,
          &.r .layer {
            border-radius: 100px;
          }

          &.b2 {
            border-radius: 2px;
          }

          .checkbox {
            position: relative;
            width: 100%;
            height: 100%;
            padding: 0;
            margin: 0;
            opacity: 0;
            cursor: pointer;
            z-index: 3;
          }

          .knobs {
            z-index: 2;

            &:before,
            &:after,
            span {
              position: absolute;
              top: 50%;
              transform: translateY(-50%);
              height: 10px;
              text-align: center;
              font-size: 22px;
              font-style: normal;
              font-weight: 400;
              line-height: 28px;
              /* 127.273% */
              letter-spacing: 0.22px;
              padding: 9px 4px;
              border-radius: 2px;
              transition: 0.3s ease all;
              color: white !important;
              display: flex;
              align-items: center;
              justify-content: center;
            }

            &:before {
              content: 'More';
              left: 20px;
            }

            &:after {
              content: 'Less';
              right: 20px;
              opacity: 0;
            }

            span {
              width: 39px;
              height: 39px;
              left: calc(126px - 3px - 39px);
              border-radius: 100%;
              background-color: white;
              // border: 5px solid green;
              z-index: 2;
            }
          }

          .layer {
            width: 100%;
            background-color: #ebf7fc;
            transition: 0.3s ease all;
            z-index: 1;
          }

          .checkbox:checked + .knobs:before {
            opacity: 0;
          }

          .checkbox:checked + .knobs:after {
            opacity: 1;
          }

          .checkbox:checked + .knobs span {
            left: 4px;
          }

          .checkbox:checked ~ .layer {
            background-color: #fcebeb;
          }
        }
      }
    }

    .right {
      @apply overflow-hidden transition-all duration-500 max-h-0 lg:max-h-none lg:absolute lg:top-0 lg:-right-[50%] lg:w-1/2 lg:h-full;

      &.active {
        @apply max-h-[1000px] lg:right-0;
      }

      .right-content {
        @apply pl-[53px] pr-[30px] pt-[50px] pb-[70px] w-full bg-lm_white medium flex flex-col justify-center gap-5 lg:h-full
        lg:justify-start lg:pt-[156px];

        p {
          @apply xl:large;
        }
      }
    }
  }
}
</style>
